import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  Table, Input, Select, Button, Typography, Tag, Empty, Spin, Segmented,
} from 'antd';
import {
  ArrowLeftOutlined, SearchOutlined, ClockCircleOutlined,
  EnvironmentOutlined, DashboardOutlined, ThunderboltOutlined,
  RiseOutlined, AimOutlined, StopOutlined, RetweetOutlined,
  CalendarOutlined, UserOutlined,
} from '@ant-design/icons';
import {
  PieChart, Pie, Cell, ResponsiveContainer, Tooltip as ReTooltip, Legend,
} from 'recharts';
import { enrollmentActions } from '../../actions/enrollmentActions';
import { batchActions } from '../../actions/batchActions';
import SpinnerLoader from '../../components/spinnerLoader';


const { Text } = Typography;

/* ── colour tokens mapped to CSS modifier classes ── */
const METRIC_COLOR_CLASS: Record<string, string> = {
  '#2563eb': 'blue',
  '#10b981': 'green',
  '#7c4dff': 'purple',
  '#06b6d4': 'cyan',
  '#ef4444': 'red',
  '#f59e0b': 'amber',
  '#7c3aed': 'indigo',
  '#dc2626': 'crimson',
};

const SessionDetails: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const enrollmentId = location.state?.enrollmentId;
  const studentId    = location.state?.studentId;
  const studentName  = location.state?.studentName;
  const serviceType = location.state?.serviceType;

  const enrollmentView        = useSelector((state: any) => state.getEnrollmentViewReducer);
  const overallStudentAnalytics = useSelector((state: any) => state.getOverallStudentAnalyticsReducer);
  const sessionAnalyticsData  = useSelector((state: any) => state.getSessionAnalyticsReducer);

  const [search, setSearch]               = useState('');
  const [statusFilter, setStatusFilter]   = useState<'ALL' | 'PRESENT' | 'ABSENT'>('ALL');
  const [typeFilter, setTypeFilter]       = useState<string>('ALL');
  const [sortOrder, setSortOrder]         = useState<'newest' | 'oldest'>('newest');
  const [selectedSession, setSelectedSession] = useState<any>(null);

  useEffect(() => {
    if (enrollmentId) {
      dispatch(enrollmentActions.getEnrollmentView({ id: enrollmentId }));
      if (studentId) {
        dispatch(batchActions.getOverallStudentAnalytics({ id: enrollmentId, studentId }));
      }
    }
  }, [enrollmentId, studentId, dispatch]);

  const viewData            = enrollmentView?.enrollmentview;
  const attendanceSummary   = viewData?.attendanceSummary;
  const isSimulationRequired = viewData?.isSimulationRequired;
  const noOfClasses         = viewData?.noOfClasses || 0;
  const overallAnalytics    = overallStudentAnalytics?.overallStudentAnalytics || null;
  const sessionAnalytics    = sessionAnalyticsData?.sessionAnalytics || null;

  const allSessions: any[] = useMemo(() => {
    const details = attendanceSummary?.attendanceDetails ?? [];
    return details.map((d: any, index: number) => ({
      key:          d._id || index,
      sessionId:    d._id,
      date:         d.sessionDate ? new Date(d.sessionDate).toISOString().split('T')[0] : '',
      rawDate:      d.sessionDate,
      time:         d.sessionTime,
      status:       d.status,
      trainingType: d.trainingType,
      markedBy:     d.markedBy,
    }));
  }, [attendanceSummary]);

  const filteredSessions = useMemo(() => {
    let list = [...allSessions];
    if (search.trim()) {
      const q = search.trim().toLowerCase();
      list = list.filter((s) =>
        (s.date || '').toLowerCase().includes(q) ||
        (s.markedBy || '').toLowerCase().includes(q) ||
        (s.time || '').toLowerCase().includes(q)
      );
    }
    if (statusFilter !== 'ALL') {
      list = list.filter((s) =>
        statusFilter === 'PRESENT'
          ? (s.status || '').toUpperCase() === 'PRESENT'
          : (s.status || '').toUpperCase() !== 'PRESENT'
      );
    }
    if (typeFilter !== 'ALL') {
      list = list.filter((s) => s.trainingType === typeFilter);
    }
    list.sort((a, b) => {
      const da = new Date(a.rawDate || 0).getTime();
      const db = new Date(b.rawDate || 0).getTime();
      return sortOrder === 'newest' ? db - da : da - db;
    });
    return list;
  }, [allSessions, search, statusFilter, typeFilter, sortOrder]);

  const presentCount  = allSessions.filter((s) => (s.status || '').toUpperCase() === 'PRESENT').length;
  const absentCount   = allSessions.length - presentCount;
  const totalSessions = allSessions.length;
  const attendancePct = totalSessions > 0 ? Math.round((presentCount / totalSessions) * 100) : 0;
  const progressPct   = noOfClasses > 0 ? Math.min(100, Math.round((totalSessions / noOfClasses) * 100)) : attendancePct;

  const statusData = [
    { name: 'Present', value: presentCount, color: '#10b981' },
    { name: 'Absent',  value: absentCount,  color: '#ef4444' },
  ].filter((d) => d.value > 0);

  const typeData = [
    { name: 'On-Road',     value: attendanceSummary?.attendedOnRoad    || 0, color: '#f59e0b' },
    { name: 'Simulation',  value: attendanceSummary?.attendedSimulation || 0, color: '#7c4dff' },
  ].filter((d) => d.value > 0);

  const handleSelectSession = (record: any) => {
    setSelectedSession(record);
    if (record.trainingType === 'ON_ROAD' && (record.status || '').toUpperCase() === 'PRESENT') {
      dispatch(batchActions.getSessionAnalytics({
        id: enrollmentId,
        studentId,
        sessionId: record.sessionId,
      }));
    }
  };

  const columns = [
    {
      title: 'Date',
      dataIndex: 'date',
      key: 'date',
      render: (text: string) => (
        <span style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
          <CalendarOutlined style={{ color: '#9ca3af', fontSize: 11 }} />
          <Text strong style={{ fontSize: 12 }}>{text || '—'}</Text>
        </span>
      ),
    },
    {
      title: 'Time',
      dataIndex: 'time',
      key: 'time',
      render: (time: string) => time
        ? <Tag color="blue" style={{ fontSize: 11, borderRadius: 6 }}><ClockCircleOutlined style={{ marginRight: 3 }} />{time}</Tag>
        : <Text type="secondary" style={{ fontSize: 12 }}>—</Text>,
    },
    ...(isSimulationRequired ? [{
      title: 'Type',
      dataIndex: 'trainingType',
      key: 'trainingType',
      render: (type: string) => type
        ? <Tag color={type === 'SIMULATION' ? 'purple' : 'orange'} style={{ fontSize: 11, borderRadius: 6 }}>
            {type === 'SIMULATION' ? 'Sim' : 'On-Road'}
          </Tag>
        : <Text type="secondary">—</Text>,
    }] : []),
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: (status: string) => {
        const present = (status || '').toUpperCase() === 'PRESENT';
        return (
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}>
            <span style={{
              width: 7, height: 7, borderRadius: '50%',
              background: present ? '#10b981' : '#ef4444',
              boxShadow: `0 0 0 2px ${present ? '#10b98130' : '#ef444430'}`,
            }} />
            <span style={{ fontSize: 12, fontWeight: 600, color: present ? '#10b981' : '#ef4444' }}>
              {present ? 'Present' : 'Absent'}
            </span>
          </span>
        );
      },
    },
    {
      title: 'Marked By',
      dataIndex: 'markedBy',
      key: 'markedBy',
      render: (text: string) => (
        <span style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
          <UserOutlined style={{ color: '#9ca3af', fontSize: 11 }} />
          <Text type="secondary" style={{ fontSize: 12 }}>{text || '—'}</Text>
        </span>
      ),
    },
  ];

  /* ── overall driving metrics config ── */
  const overallMetrics = [
    { title: 'Distance',       value: `${overallAnalytics?.overallDistance           ?? 0} km`,   icon: <EnvironmentOutlined />,  color: '#2563eb' },
    { title: 'Drive Time',     value: `${overallAnalytics?.overallTime               ?? 0} min`,  icon: <ClockCircleOutlined />,  color: '#10b981' },
    { title: 'Avg Dist/Session', value: `${overallAnalytics?.averageDistancePerSession ?? 0} km`, icon: <DashboardOutlined />,    color: '#7c4dff' },
    { title: 'Avg Speed',      value: `${overallAnalytics?.averageSpeedPerSession     ?? 0} km/h`,icon: <ThunderboltOutlined />,  color: '#06b6d4' },
    { title: 'Max Speed',      value: `${overallAnalytics?.overallMaxSpeed            ?? 0} km/h`,icon: <RiseOutlined />,         color: '#ef4444' },
  ];

  if (enrollmentView?.isLoading && !viewData) {
    return <div className="view-loading"><SpinnerLoader /></div>;
  }

  if (!enrollmentId) {
    return (
      <div className="sd-page">
        <Button icon={<ArrowLeftOutlined />} onClick={() => navigate('/enrollments')}>Back</Button>
        <Empty description="No session context found" style={{ marginTop: 40 }} />
      </div>
    );
  }

  const sessionPresent = selectedSession && (selectedSession.status || '').toUpperCase() === 'PRESENT';

  return (
    <div className="sd-page">

      {/* ── Back ── */}
      <button className="sd-back-btn" onClick={() => navigate('/enrollments/view', { state: { enrollmentId, serviceType } })}>
        <ArrowLeftOutlined style={{ fontSize: 13 }} />
        Back to Enrollment
      </button>

      {/* ── Page heading ── */}
      <div className="sd-page-header">
        <div>
          <h2 className="sd-page-title">Session Details</h2>
          {studentName && (
            <span className="sd-student-name">
              <UserOutlined style={{ marginRight: 5 }} />{studentName}
            </span>
          )}
        </div>

        {/* quick stat badges */}
        <div className="sd-badge-strip">
          <div className="sd-badge sd-badge--present">
            {presentCount} <span className="sd-badge-sublabel">Present</span>
          </div>
          <div className="sd-badge sd-badge--absent">
            {absentCount} <span className="sd-badge-sublabel">Absent</span>
          </div>
          <div className="sd-badge sd-badge--total">
            {totalSessions} <span className="sd-badge-sublabel">Total</span>
          </div>
        </div>
      </div>

      {/* ── Two-column layout ── */}
      <div className="sd-split">

        {/* ── LEFT : Session list ── */}
        <div className="sd-panel">
          <span className="sd-section-label">Session List</span>

          <Input
            allowClear
            prefix={<SearchOutlined style={{ color: '#9ca3af' }} />}
            placeholder="Search by date, time or marked by…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="sd-search-input"
          />

          <div className="sd-toolbar-row">
            <Segmented
              value={statusFilter}
              onChange={(val) => setStatusFilter(val as any)}
              options={[
                { label: 'All',     value: 'ALL'     },
                { label: 'Present', value: 'PRESENT' },
                { label: 'Absent',  value: 'ABSENT'  },
              ]}
            />
            {isSimulationRequired && (
              <Select
                value={typeFilter}
                onChange={setTypeFilter}
                style={{ width: 130 }}
                options={[
                  { label: 'All Types',   value: 'ALL'        },
                  { label: 'On-Road',     value: 'ON_ROAD'    },
                  { label: 'Simulation',  value: 'SIMULATION' },
                ]}
              />
            )}
            <Select
              value={sortOrder}
              onChange={(v) => setSortOrder(v as any)}
              style={{ width: 130 }}
              options={[
                { label: 'Newest first', value: 'newest' },
                { label: 'Oldest first', value: 'oldest' },
              ]}
            />
          </div>

        <Table
            size="small"
            columns={columns}
            dataSource={filteredSessions}
            rowKey="key"
            className="sdx-table"
            pagination={{
              pageSize: 8,
              showSizeChanger: false,
              size: 'small',
              hideOnSinglePage: false,
              showTotal: (total, range) => `${range[0]}–${range[1]} of ${total}`,
            }}
            onRow={(record) => ({
              onClick: () => handleSelectSession(record),
              className: selectedSession?.key === record.key ? 'sd-row-selected' : '',
              style: { cursor: 'pointer', transition: 'background 0.15s' },
            })}
            locale={{ emptyText: <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No sessions found" /> }}
            style={{ marginTop: 2 }}
          />
        </div>

        {/* ── RIGHT : Statistics ── */}
        <div className="sd-panel">
          <span className="sd-section-label">Statistics</span>

          {/* Progress ring */}
          <div className="sd-ring-wrap">
            <div
              className="sd-ring-outer"
              style={{ background: `conic-gradient(#4f6ef7 ${progressPct * 3.6}deg, #e8ecf4 0)` }}
            >
              <div className="sd-ring-inner">
                <span className="sd-ring-pct">{progressPct}%</span>
              </div>
            </div>
            <div className="sd-ring-meta">
              <div className="sd-ring-meta-title">
                {noOfClasses > 0 ? `${totalSessions} / ${noOfClasses} classes` : `${totalSessions} sessions`}
              </div>
              <div className="sd-ring-meta-sub">
                {noOfClasses > 0 ? 'Course progress' : 'Attendance rate'}
              </div>
            </div>
          </div>

          {/* Stat strip */}
          <div className="sd-stat-strip">
            {[
              { label: 'Present',    value: presentCount,  mod: 'present'    },
              { label: 'Absent',     value: absentCount,   mod: 'absent'     },
              { label: 'Total',      value: `${totalSessions}${noOfClasses > 0 ? `/${noOfClasses}` : ''}`, mod: 'total' },
              { label: 'Attendance', value: `${attendancePct}%`, mod: 'attendance' },
            ].map((s) => (
              <div key={s.label} className={`sd-stat-box sd-stat-box--${s.mod}`}>
                <span className={`sd-stat-value sd-stat-value--${s.mod}`}>{s.value}</span>
                <span className="sd-stat-label">{s.label}</span>
              </div>
            ))}
          </div>

          {/* Charts */}
          <div className="sd-chart-row">
            <div className="sd-chart-box">
              <div className="sd-chart-title">Status Distribution</div>
              {statusData.length > 0 ? (
                <ResponsiveContainer width="100%" height={160}>
                  <PieChart>
                    <Pie data={statusData} dataKey="value" nameKey="name" innerRadius={40} outerRadius={62} paddingAngle={3}>
                      {statusData.map((e, i) => <Cell key={i} fill={e.color} />)}
                    </Pie>
                    <ReTooltip />
                    <Legend iconType="circle" iconSize={8} />
                  </PieChart>
                </ResponsiveContainer>
              ) : <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No data" />}
            </div>

            {isSimulationRequired && typeData.length > 0 && (
              <div className="sd-chart-box">
                <div className="sd-chart-title">Training Type</div>
                <ResponsiveContainer width="100%" height={160}>
                  <PieChart>
                    <Pie data={typeData} dataKey="value" nameKey="name" innerRadius={40} outerRadius={62} paddingAngle={3}>
                      {typeData.map((e, i) => <Cell key={i} fill={e.color} />)}
                    </Pie>
                    <ReTooltip />
                    <Legend iconType="circle" iconSize={8} />
                  </PieChart>
                </ResponsiveContainer>
              </div>
            )}
          </div>

          {/* Overall driving metrics */}
          <div className="sd-chart-title">Overall Driving Metrics</div>
          <div className="sd-metrics-grid">
            {overallMetrics.map((m, i) => {
              const mod = METRIC_COLOR_CLASS[m.color];
              return (
                <div key={i} className={`sd-metric-box sd-metric-box--${mod}`}>
                  <span className={`sd-metric-icon sd-metric-icon--${mod}`}>{m.icon}</span>
                  <div>
                    <div className={`sd-metric-value sd-metric-value--${mod}`}>{m.value}</div>
                    <div className="sd-metric-label">{m.title}</div>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Selected session detail */}
          {selectedSession && (
            <>
              <div className="sd-divider" />

              <div className="sd-session-banner">
                <span className={`sd-session-banner-dot sd-session-banner-dot--${sessionPresent ? 'present' : 'absent'}`} />
                <span className="sd-session-banner-text">
                  Session · {selectedSession.date}
                  {selectedSession.time && (
                    <span className="sd-session-banner-sub">· {selectedSession.time}</span>
                  )}
                </span>
                <Tag
                  color={sessionPresent ? 'success' : 'error'}
                  style={{ marginLeft: 'auto', borderRadius: 8, fontSize: 11 }}
                >
                  {sessionPresent ? 'Present' : 'Absent'}
                </Tag>
              </div>

              {selectedSession.trainingType === 'ON_ROAD' && sessionPresent ? (
                <Spin spinning={sessionAnalyticsData?.isLoading}>
                  {sessionAnalytics ? (
                    <div className="sd-metrics-grid">
                      {[
                        { title: 'Distance',    value: `${sessionAnalytics?.overallDistance  ?? 0} km`,   icon: <EnvironmentOutlined />, color: '#2563eb' },
                        { title: 'Time',        value: `${sessionAnalytics?.overallTime      ?? 0} min`,  icon: <ClockCircleOutlined />, color: '#10b981' },
                        { title: 'Avg Speed',   value: `${sessionAnalytics?.overallAvgSpeed  ?? 0} km/h`, icon: <DashboardOutlined />,   color: '#7c4dff' },
                        { title: 'Max Speed',   value: `${sessionAnalytics?.overallMaxSpeed  ?? 0} km/h`, icon: <RiseOutlined />,        color: '#ef4444' },
                        { title: 'Left Turns',  value: sessionAnalytics?.leftTurns  ?? 0,                 icon: <RetweetOutlined />,     color: '#f59e0b' },
                        { title: 'Right Turns', value: sessionAnalytics?.rightTurns ?? 0,                 icon: <RetweetOutlined />,     color: '#06b6d4' },
                        { title: 'U-Turns',     value: sessionAnalytics?.uTurns     ?? 0,                 icon: <AimOutlined />,         color: '#7c3aed' },
                        { title: 'Stops',       value: sessionAnalytics?.totalStops ?? 0,                 icon: <StopOutlined />,        color: '#dc2626' },
                      ].map((m, i) => {
                        const mod = METRIC_COLOR_CLASS[m.color];
                        return (
                          <div key={i} className={`sd-metric-box sd-metric-box--${mod}`}>
                            <span className={`sd-metric-icon sd-metric-icon--${mod}`}>{m.icon}</span>
                            <div>
                              <div className={`sd-metric-value sd-metric-value--${mod}`}>{m.value}</div>
                              <div className="sd-metric-label">{m.title}</div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  ) : (
                    <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No analytics for this session" />
                  )}
                </Spin>
              ) : (
                <div className="sd-note-box">
                  Detailed driving analytics are available only for attended on-road sessions.
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default SessionDetails;